import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Divider, IconButton, Typography } from "@mzc-pdc/ui";
import { IconIndexing, IconCompleteCircle, IconErrorCircle, IconRetry } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import Tooltip from "@components_v2/Tooltip/tooltip";
import { ASSET_SEARCH_INDEX_ERROR_CODE, ASSET_SEARCH_INDEX_STATUS } from "@constants";

/**
 * 원시 인덱싱 상태 문자열을 3종 표준값(COMPLETED/PENDING/FAILED)으로 정규화한다.
 * NOT_SUPPORTED(미지원)는 표시상 실패와 동일하게 취급해 FAILED로 정규화한다.
 * 미지/누락 값은 진행 중(PENDING)으로 방어 처리한다.
 * @param {string} raw
 * @returns {"COMPLETED"|"PENDING"|"FAILED"}
 */
export const getAssetSearchIndexStatus = (raw) => {
    const normalized = String(raw ?? "")
        .trim()
        .toUpperCase();

    switch (normalized) {
        case ASSET_SEARCH_INDEX_STATUS.COMPLETED:
            return ASSET_SEARCH_INDEX_STATUS.COMPLETED;
        case ASSET_SEARCH_INDEX_STATUS.FAILED:
        case ASSET_SEARCH_INDEX_STATUS.NOT_SUPPORTED:
            return ASSET_SEARCH_INDEX_STATUS.FAILED;
        case ASSET_SEARCH_INDEX_STATUS.PENDING:
        default:
            return ASSET_SEARCH_INDEX_STATUS.PENDING;
    }
};

const STATUS_CONFIG = {
    [ASSET_SEARCH_INDEX_STATUS.COMPLETED]: {
        sx: {
            color: theme.palette.success.main,
            backgroundColor: theme.palette.success[50],
            borderColor: theme.palette.success[200],
        },
    },
    [ASSET_SEARCH_INDEX_STATUS.PENDING]: {
        sx: {
            color: theme.palette.info.main,
            backgroundColor: theme.palette.info[50],
            borderColor: theme.palette.info[200],
        },
    },
    [ASSET_SEARCH_INDEX_STATUS.FAILED]: {
        sx: {
            color: theme.palette.error.main,
            backgroundColor: theme.palette.error[50],
            borderColor: theme.palette.error[200],
        },
    },
};

const SIZE_CONFIG = {
    sm: { iconSize: 12, sx: { width: 20, height: 20, justifyContent: "center" } },
    md: { iconSize: 12, sx: { height: 20, px: 0.5, gap: 0.25 } },
    lg: { iconSize: 16, sx: { height: 25, px: 1, gap: 0.75 } },
};

// 완료/진행중 툴팁 사유 문구 키(common::msg::). 실패는 error.code 기반으로 별도 매핑한다.
const INFO_MESSAGE_KEY = {
    [ASSET_SEARCH_INDEX_STATUS.COMPLETED]: "You can find this asset in AI search.",
    [ASSET_SEARCH_INDEX_STATUS.PENDING]: "AI search indexing is in progress.",
};

// 실패 사유는 백엔드 메시지가 다양해 파악 불가 → error.code를 보고 프론트에서 문구를 매핑한다.
const ERROR_REASON_KEY = {
    // 미지원(재시도 불가)
    [ASSET_SEARCH_INDEX_ERROR_CODE.UNSUPPORTED_MEDIA_TYPE]: "This is an unsupported media type.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.UNSUPPORTED_SOURCE_FORMAT]: "This is an unsupported source format.",
    // 실패(재시도 가능)
    [ASSET_SEARCH_INDEX_ERROR_CODE.DISPATCH_PREPARE_FAILED]: "Failed to prepare indexing.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.METADATA_EXTRACT_FAILED]: "Failed to extract metadata.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.SOURCE_PREPARE_FAILED]: "Failed to create the source.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.SOURCE_SUPPORT_CHECK_FAILED]: "Failed to check source support.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.SOURCE_EMBED_FAILED]: "Failed to analyze the source.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.TITAN_EMBED_FAILED]: "Failed to analyze metadata.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.DOCUMENT_INDEX_FAILED]: "Failed to create the search index.",
    [ASSET_SEARCH_INDEX_ERROR_CODE.INTERNAL_ERROR]: "An internal error occurred.",
};
const DEFAULT_FAILURE_REASON_KEY = "AI search indexing failed.";

/**
 * 자산의 AI 검색모드 인덱싱 상태를 아이콘 pill로 표시하는 공통 컴포넌트.
 * 호버 시 상태별 리치 툴팁(타이틀 + 상태 칩 / 구분선 / 상태별 사유)을 항상 제공하며,
 * 실패 사유는 errorCode 기반으로 매핑한다. showLabel이 true면 pill 우측에 상태 텍스트를
 * 함께 노출하고, 텍스트 호버에도 동일한 툴팁이 뜬다(목록 테이블 뷰용).
 *
 * @param {object} props
 * @param {string} props.status - 원시 인덱싱 상태 문자열(대소문자 무관). 내부에서 정규화.
 * @param {"sm"|"md"|"lg"} [props.size="md"] - 픽셀 크기 + 우측 상태 아이콘 노출(sm=미노출, md·lg=노출).
 * @param {boolean} [props.showLabel=false] - pill 우측에 상태 텍스트 라벨 노출(재시도 버튼과 병용 불가).
 * @param {string} [props.errorCode] - 실패 상태 사유 문구를 결정하는 indexDoc.error.code. 미지정 시 일반 실패 문구.
 * @param {function} [props.onRetry] - 전달 시 실패 상태에서 배지 우측에 재시도 버튼 + 툴팁 재시도 안내 노출(상세 전용). 클릭하면 호출된다.
 * @param {boolean} [props.retryDisabled=false] - 재시도 버튼 비활성화(예: 재요청 진행 중 중복 차단).
 * @param {object} [props.sx] - 추가 스타일.
 */
const AssetSearchIndexStatus = ({
    status,
    size = "md",
    showLabel = false,
    errorCode,
    onRetry,
    retryDisabled = false,
    sx,
}) => {
    const { t } = useTranslation();

    const normalizedStatus = getAssetSearchIndexStatus(status);
    const statusConfig = STATUS_CONFIG[normalizedStatus];
    const sizeConfig = SIZE_CONFIG[size] ?? SIZE_CONFIG.md;
    const { iconSize } = sizeConfig;
    const showStatusIcon = sizeConfig !== SIZE_CONFIG.sm;

    // normalizedStatus는 NOT_SUPPORTED를 FAILED로 정규화하므로 === FAILED로 실패/미지원을 모두 커버(대소문자·공백 안전).
    const isFailed = normalizedStatus === ASSET_SEARCH_INDEX_STATUS.FAILED;
    // 재시도 버튼은 실패 상태 + onRetry 전달(상세 전용)일 때 노출. 미지원 등 재시도 불가 여부는 retryDisabled로 제어.
    const showRetry = isFailed && typeof onRetry === "function";

    const searchModeLabel = t("common::label::AI search mode", "AI search mode");
    const statusLabel = t(`common::label::${normalizedStatus}`, normalizedStatus);
    const failureReasonKey = ERROR_REASON_KEY[errorCode] ?? DEFAULT_FAILURE_REASON_KEY;
    const failureReason = t(`common::msg::${failureReasonKey}`, failureReasonKey);
    // 실패 상태에서는 사유까지 읽어주도록 aria-label에 포함(리치 툴팁은 시각 전용).
    const ariaLabel = isFailed
        ? `${searchModeLabel} ${statusLabel} ${failureReason}`
        : `${searchModeLabel} ${statusLabel}`;

    const handleRetryClick = (e) => {
        e.stopPropagation();
        if (retryDisabled) return;
        onRetry();
    };

    const renderStatusIcon = () => {
        switch (normalizedStatus) {
            case ASSET_SEARCH_INDEX_STATUS.COMPLETED:
                return <IconCompleteCircle size={iconSize} color={"inherit"} />;
            case ASSET_SEARCH_INDEX_STATUS.FAILED:
                return <IconErrorCircle size={iconSize} color={"inherit"} />;
            case ASSET_SEARCH_INDEX_STATUS.PENDING:
            default:
                return <CircularProgress size={iconSize} color={"inherit"} />;
        }
    };

    const tooltipContent = (
        <Box sx={{ width: 180, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                    sx={{ fontSize: 12, fontWeight: 500, color: theme.palette.common.white, whiteSpace: "nowrap" }}
                >
                    {searchModeLabel}
                </Typography>
                <Box
                    component={"span"}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        flexShrink: 0,
                        px: 0.5,
                        borderRadius: "100px",
                        backgroundColor: statusConfig.sx.color,
                        color: theme.palette.common.white,
                        fontSize: 12,
                        lineHeight: 1.5,
                        whiteSpace: "nowrap",
                    }}
                >
                    {statusLabel}
                </Box>
            </Box>
            <Divider sx={{ borderColor: theme.palette.greySecondary[500] }} />
            {isFailed ? (
                <>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <Typography sx={{ fontSize: 11, color: theme.palette.common.white, opacity: 0.8 }}>
                            {t("common::label::Failure reason", "Failure reason")}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: theme.palette.common.white }}>
                            {failureReason}
                        </Typography>
                    </Box>
                    {showRetry && !retryDisabled && (
                        <>
                            <Divider sx={{ borderColor: theme.palette.greySecondary[500] }} />
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 0.5,
                                    color: theme.palette.greySecondary[200],
                                }}
                            >
                                <IconRetry size={16} color={"inherit"} />
                                <Typography sx={{ fontSize: 11, color: theme.palette.greySecondary[200] }}>
                                    {t(
                                        "common::msg::Click the button on the badge to retry.",
                                        "Click the button on the badge to retry.",
                                    )}
                                </Typography>
                            </Box>
                        </>
                    )}
                </>
            ) : (
                <Typography sx={{ fontSize: 11, color: theme.palette.greySecondary[200] }}>
                    {t(`common::msg::${INFO_MESSAGE_KEY[normalizedStatus]}`, INFO_MESSAGE_KEY[normalizedStatus])}
                </Typography>
            )}
        </Box>
    );

    const retryLabel = t("common::label::Retry AI search indexing", "Retry AI search indexing");

    // 상태 리치 툴팁 부착 헬퍼. 재시도 캡슐에서는 badge 세그먼트에만 감싸 재시도 버튼 툴팁과 중첩되지 않게 한다.
    const withStatusTooltip = (node) => {
        return (
            <Tooltip
                title={tooltipContent}
                placement={"bottom"}
                componentsProps={{
                    tooltip: {
                        sx: {
                            // 디자인: 컨테이너가 콘텐츠를 px 8 / py 4로 감싼다.
                            py: 0.5,
                            px: 1,
                            backgroundColor: theme.palette.greySecondary[600],
                        },
                    },
                }}
            >
                {node}
            </Tooltip>
        );
    };

    // 단일 pill — 카드/목록 등 재시도 버튼이 없는 모든 경우.
    if (!showRetry) {
        const pill = (
            <Box
                role={"img"}
                aria-label={ariaLabel}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    borderRadius: "100px",
                    border: 1,
                    ...statusConfig.sx,
                    ...sizeConfig.sx,
                    ...(!showLabel && sx),
                }}
            >
                <IconIndexing size={iconSize} color={"inherit"} />
                {showStatusIcon && renderStatusIcon()}
            </Box>
        );

        // 라벨 포함 시 pill + 텍스트를 하나의 트리거로 묶어 텍스트 호버에도 툴팁이 뜨게 한다.
        return withStatusTooltip(
            showLabel ? (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, ...sx }}>
                    {pill}
                    <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: statusConfig.sx.color }}>
                        {statusLabel}
                    </Typography>
                </Box>
            ) : (
                pill
            ),
        );
    }

    // [에러 배지 + 재시도 버튼] 2세그먼트 캡슐(상세 전용).
    // 캡슐 테두리·높이는 컨테이너가 소유하고 세그먼트 구분선은 badge의 우측 테두리 —
    // 두 세그먼트가 stretch로 같은 높이를 갖게 해 구분선 틀어짐을 방지한다.
    return (
        <Box
            role={"group"}
            aria-label={ariaLabel}
            sx={{
                display: "inline-flex",
                alignItems: "stretch",
                height: 24,
                boxSizing: "border-box",
                border: 1,
                borderColor: statusConfig.sx.borderColor,
                borderRadius: "100px",
                overflow: "hidden",
                ...sx,
            }}
        >
            {withStatusTooltip(
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1,
                        color: statusConfig.sx.color,
                        backgroundColor: statusConfig.sx.backgroundColor,
                        borderRight: 1,
                        borderColor: statusConfig.sx.borderColor,
                    }}
                >
                    <IconIndexing size={iconSize} color={"inherit"} />
                    {renderStatusIcon()}
                </Box>,
            )}
            <Tooltip title={retryLabel} placement={"top"}>
                <Box sx={{ display: "inline-flex", alignItems: "center", pr: 0.5 }}>
                    <IconButton
                        circled
                        size={"small"}
                        aria-label={retryLabel}
                        aria-disabled={retryDisabled}
                        onClick={handleRetryClick}
                        sx={{
                            width: 22,
                            height: 22,
                            p: 0,
                            ...(retryDisabled && { opacity: 0.5, pointerEvents: "none" }),
                        }}
                    >
                        <IconRetry size={16} />
                    </IconButton>
                </Box>
            </Tooltip>
        </Box>
    );
};

AssetSearchIndexStatus.propTypes = {
    status: PropTypes.string.isRequired,
    size: PropTypes.oneOf(["sm", "md", "lg"]),
    showLabel: PropTypes.bool,
    errorCode: PropTypes.string,
    onRetry: PropTypes.func,
    retryDisabled: PropTypes.bool,
    sx: PropTypes.object,
};

export default AssetSearchIndexStatus;
